import type { EvaluationCriteriaSelectedItem, EvaluationCriterion, Region } from 'app/types/evaluationCriteria';
import dataManagerService from 'core/chatbot/services/dataManagerService';
import { useOrganization } from 'core/hooks/useOrganization';
import * as coreEntities from 'core/store/index';
import type { TFunction } from 'i18next';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

type LegacyValueItem = EvaluationCriteriaSelectedItem | string;

type UseSelectLearningObjectivesTanAIParams = {
  disciplineId?: string;
  educationLevel?: string;
  educationYear?: string;
  onChange: (next: EvaluationCriteriaSelectedItem[]) => void;
  placeholder?: string;
  value: LegacyValueItem[];
};

type RegionOption = { label: string; value: string };

type DisciplineOption = { label: string; value: string };

type Discipline = {
  guid: string;
  name?: string;
  discipline?: string;
};

type EducationYearValue = {
  educationLevel: string;
  educationYear: string;
  educationLevelLabel?: string;
  educationYearLabel?: string;
};

type UseSelectLearningObjectivesTanAIReturn = {
  t: TFunction;
  isOpen: boolean;
  isLoadingRegions: boolean;
  isLoadingCriteria: boolean;
  hasSearched: boolean;
  isSearchEnabled: boolean;
  regionOptions: RegionOption[];
  selectedRegionOption: RegionOption | null;
  disciplineOptions: DisciplineOption[];
  selectedDisciplineOption: DisciplineOption | null;
  criteria: EvaluationCriterion[];
  normalizedValue: EvaluationCriteriaSelectedItem[];
  pendingSelection: EvaluationCriteriaSelectedItem[];
  resolvedPlaceholder: string;
  educationYearValue: EducationYearValue;
  showMoreLabel: string;
  showLessLabel: string;
  handleOpenDialog: () => void;
  handleInputKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  handleRegionChange: (option: RegionOption | null) => void;
  handleDisciplineChange: (option: DisciplineOption | null) => void;
  handleEducationYearChange: (data: Partial<EducationYearValue> | null | undefined) => void;
  handleSearch: () => Promise<void>;
  handleToggleById: (id: string) => void;
  handleRemoveTag: (id: string) => void;
  handleSave: () => void;
  handleCancel: () => void;
  isCriterionSelected: (id: string) => boolean;
};

const COUNTRY_CODE = 'ES';

function useSelectLearningObjectivesTanAI({
  disciplineId,
  educationLevel,
  educationYear,
  onChange,
  placeholder,
  value,
}: UseSelectLearningObjectivesTanAIParams): UseSelectLearningObjectivesTanAIReturn {
  const { t } = useTranslation(['chatbot', 'common', 'actions']);
  const { client } = useOrganization();
  const dispatch = useDispatch();

  const disciplines: Discipline[] = useSelector((state: unknown) => coreEntities.educationLevels.selectors.getDisciplines(state)) || [];

  // Backwards-compat: accept legacy `string[]` values and normalize them to objects.
  const normalizedValue: EvaluationCriteriaSelectedItem[] = useMemo(
    () => (value ?? []).map((item) => (typeof item === 'string' ? { id: item, description: item } : item)),
    [value],
  );

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [regionCode, setRegionCode] = useState<string>('');
  const [regions, setRegions] = useState<Region[]>([]);
  const [criteria, setCriteria] = useState<EvaluationCriterion[]>([]);
  const [isLoadingRegions, setIsLoadingRegions] = useState<boolean>(false);
  const [isLoadingCriteria, setIsLoadingCriteria] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [hasFetchedRegions, setHasFetchedRegions] = useState<boolean>(false);
  const [disciplineIdState, setDisciplineIdState] = useState<string>(disciplineId ?? '');
  const [educationYearValue, setEducationYearValue] = useState<EducationYearValue>({
    educationLevel: educationLevel ?? '',
    educationYear: educationYear ?? '',
  });
  // Pending selection inside the dialog. We commit it to the parent only on Save.
  const [pendingSelection, setPendingSelection] = useState<EvaluationCriteriaSelectedItem[]>(normalizedValue);

  // Load disciplines (and education levels) from Redux when they are missing
  useEffect(() => {
    if (!disciplines || disciplines.length === 0) {
      dispatch(coreEntities.educationLevels.actions.getEducationLevels({ client }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, client]);

  // Sync internal discipline state when prop arrives after mount
  useEffect(() => {
    const next = disciplineId ?? '';
    setDisciplineIdState((prev) => (prev === next ? prev : next));
  }, [disciplineId]);

  // Sync internal state when props arrive after mount (e.g. async load from parent form)
  useEffect(() => {
    setEducationYearValue((prev) => {
      const nextLevel = educationLevel ?? '';
      const nextYear = educationYear ?? '';
      if (prev.educationLevel === nextLevel && prev.educationYear === nextYear) {
        return prev;
      }
      return {
        ...prev,
        educationLevel: nextLevel,
        educationYear: nextYear,
      };
    });
  }, [educationLevel, educationYear]);

  // Keep pendingSelection in sync with the parent value whenever the dialog is closed.
  // When the dialog is open we don't overwrite pending changes that the user is editing.
  useEffect(() => {
    if (isOpen) return;
    setPendingSelection(normalizedValue);
  }, [normalizedValue, isOpen]);

  // Tracks the most recent search request so stale responses can be discarded.
  const searchRequestIdRef = useRef<number>(0);

  const resolvedPlaceholder = placeholder ?? t('chatbot:teacher_tools_select_evaluation_criteria_no_selection_placeholder');
  const showMoreLabel = t('chatbot:teacher_tools_select_evaluation_criteria_show_more');
  const showLessLabel = t('chatbot:teacher_tools_select_evaluation_criteria_show_less');

  // Fetch regions only the first time the dialog opens
  useEffect(() => {
    let isCancelled = false;

    async function fetchRegions(): Promise<void> {
      setIsLoadingRegions(true);
      try {
        const result = await dataManagerService.getRegions(COUNTRY_CODE);
        if (isCancelled) return;
        setRegions(Array.isArray(result) ? result : []);
        // Only mark as fetched on success so a failed request retries when the dialog reopens.
        setHasFetchedRegions(true);
      } catch (error) {
        console.warn('Error fetching evaluation criteria regions:', error);
        if (isCancelled) return;
        setRegions([]);
      } finally {
        if (!isCancelled) {
          setIsLoadingRegions(false);
        }
      }
    }

    if (isOpen && !hasFetchedRegions) {
      fetchRegions();
    }

    return () => {
      isCancelled = true;
    };
  }, [isOpen, hasFetchedRegions]);

  const regionOptions: RegionOption[] = regions.map((region) => ({
    label: region?.name,
    value: region?.code,
  }));

  const selectedRegionOption: RegionOption | null = regionOptions.find((option) => option.value === regionCode) ?? null;

  const disciplineOptions: DisciplineOption[] = disciplines.map((discipline) => ({
    label: discipline?.discipline ?? discipline?.name ?? '',
    value: discipline?.guid,
  }));

  const selectedDisciplineOption: DisciplineOption | null = disciplineOptions.find((option) => option.value === disciplineIdState) ?? null;

  const isSearchEnabled = Boolean(regionCode && disciplineIdState);

  function handleOpenDialog(): void {
    // Reset pending selection to the committed value so unconfirmed changes are discarded.
    setPendingSelection(normalizedValue);
    setIsOpen(true);
  }

  function handleInputKeyDown(event: React.KeyboardEvent<HTMLButtonElement>): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOpenDialog();
    }
  }

  // Reset the search outcome so stale criteria from a previous search are not shown after changing a filter.
  function resetSearchResults(): void {
    setHasSearched(false);
    setCriteria([]);
  }

  function handleRegionChange(option: RegionOption | null): void {
    setRegionCode(option?.value ?? '');
    resetSearchResults();
  }

  function handleDisciplineChange(option: DisciplineOption | null): void {
    setDisciplineIdState(option?.value ?? '');
    resetSearchResults();
  }

  function handleEducationYearChange(data: Partial<EducationYearValue> | null | undefined): void {
    setEducationYearValue({
      educationLevel: data?.educationLevel ?? '',
      educationYear: data?.educationYear ?? '',
      educationLevelLabel: data?.educationLevelLabel,
      educationYearLabel: data?.educationYearLabel,
    });
    resetSearchResults();
  }

  const handleSearch = useCallback(async (): Promise<void> => {
    if (!isSearchEnabled) return;

    searchRequestIdRef.current += 1;
    const requestId = searchRequestIdRef.current;

    setIsLoadingCriteria(true);
    setHasSearched(true);

    try {
      const result = await dataManagerService.getEvaluationCriteria({
        disciplineId: disciplineIdState,
        regionCode,
        educationYear: educationYearValue?.educationYear,
      });
      // Discard the response if a newer search has been triggered in the meantime.
      if (requestId !== searchRequestIdRef.current) return;
      setCriteria(Array.isArray(result) ? result : []);
    } catch (error) {
      console.warn('Error fetching evaluation criteria:', error);
      if (requestId !== searchRequestIdRef.current) return;
      setCriteria([]);
    } finally {
      if (requestId === searchRequestIdRef.current) {
        setIsLoadingCriteria(false);
      }
    }
  }, [isSearchEnabled, disciplineIdState, regionCode, educationYearValue]);

  function isCriterionSelected(id: string): boolean {
    return pendingSelection.some((item) => item?.id === id);
  }

  function handleToggleById(id: string): void {
    const exists = isCriterionSelected(id);
    if (exists) {
      setPendingSelection((prev) => prev.filter((item) => item?.id !== id));
      return;
    }
    const criterion = criteria.find((c) => c?.id === id);
    if (!criterion) return;
    setPendingSelection((prev) => [...prev, { id: criterion.id, description: criterion.description }]);
  }

  function handleRemoveTag(id: string): void {
    // Removing a tag from outside the dialog is applied immediately.
    onChange(normalizedValue.filter((item) => item?.id !== id));
  }

  function handleSave(): void {
    onChange(pendingSelection);
    setIsOpen(false);
  }

  function handleCancel(): void {
    setPendingSelection(normalizedValue);
    setIsOpen(false);
  }

  return {
    t,
    isOpen,
    isLoadingRegions,
    isLoadingCriteria,
    hasSearched,
    isSearchEnabled,
    regionOptions,
    selectedRegionOption,
    disciplineOptions,
    selectedDisciplineOption,
    criteria,
    normalizedValue,
    pendingSelection,
    resolvedPlaceholder,
    educationYearValue,
    showMoreLabel,
    showLessLabel,
    handleOpenDialog,
    handleInputKeyDown,
    handleRegionChange,
    handleDisciplineChange,
    handleEducationYearChange,
    handleSearch,
    handleToggleById,
    handleRemoveTag,
    handleSave,
    handleCancel,
    isCriterionSelected,
  };
}

export default useSelectLearningObjectivesTanAI;
